import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  DataSource,
  RealtimeUsage,
  SOURCE_TYPE_LABELS,
  isRealtimeSourceType,
} from '../../types/dataSource';
import { relativeTime } from '../../utils/datetime';

/** Adapters whose provider bills per request; the table shows their spend. */
const METERED_ADAPTERS = new Set(['xweather_lightning']);

const ADAPTER_LABELS: Record<string, string> = {
  arcgis_feature_server: 'ArcGIS Feature Server',
  arcgis_authenticated_feature: 'ArcGIS Authenticated',
  arcgis_image_server: 'ArcGIS Image Server',
  shapefile: 'Shapefile',
  raster_with_lookup: 'Raster + Lookup',
  nafi_multi_shapefile: 'NAFI Multi-Shapefile',
  mapbox_vector_tiles: 'Mapbox Vector Tiles',
  overture: 'Overture',
  firms_hotspots: 'NASA FIRMS Hotspots',
  dea_hotspots: 'DEA Hotspots',
  xweather_lightning: 'Xweather Lightning',
  synthetic_lightning: 'Synthetic Lightning',
  simulated_hotspot: 'Simulated Hotspots (manual)',
  simulated_lightning: 'Simulated Lightning (manual)',
  nsw_rfs_hazards: 'NSW RFS Major Incidents',
  vic_emv_hazards: 'VIC Emergency Incidents & Warnings',
  qld_qfes_hazards: 'QLD QFES Bushfire Alerts',
  sa_cfs_hazards: 'SA CFS Current Incidents',
  wa_dfes_hazards: 'WA EmergencyWA Incidents & Warnings',
  act_esa_hazards: 'ACT ESA Current Incidents',
};

function RealtimeStatusChip({ source }: { source: DataSource }) {
  const status = source.realtime_status;
  if (!status) return <span>—</span>;

  const isIdle = (status.last_error ?? '').toLowerCase().includes('idle');
  if (isIdle) {
    return <Chip label="Idle" size="small" />;
  }
  if (status.consecutive_failures > 0) {
    return (
      <Tooltip title={status.last_error ?? 'Unknown error'} arrow>
        <Chip label="Error" size="small" color="error" />
      </Tooltip>
    );
  }
  if (status.last_success_at) {
    return <Chip label="OK" size="small" color="success" />;
  }
  return <Chip label="Pending" size="small" />;
}

function UsageCell({ usage }: { usage: RealtimeUsage | null | undefined }) {
  if (!usage) {
    return (
      <Typography variant="body2" color="text.secondary">
        0
      </Typography>
    );
  }
  const byDay = new Map(usage.days.map((d) => [d.day, d]));
  const last7: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    last7.push(d.toISOString().slice(0, 10));
  }
  const avg7 = Math.round(
    last7.reduce((sum, day) => sum + (byDay.get(day)?.accesses ?? 0), 0) / 7,
  );
  const detail = (
    <table style={{ borderCollapse: 'collapse' }}>
      <tbody>
        {usage.days.slice(0, 14).map((d) => (
          <tr key={d.day}>
            <td style={{ paddingRight: 12 }}>{d.day}</td>
            <td style={{ textAlign: 'right', paddingRight: 12 }}>{d.accesses.toLocaleString()}</td>
            <td style={{ opacity: 0.8 }}>
              {d.active_polls > 0 ? `${d.active_polls} polls with strikes` : 'quiet'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Tooltip title={detail} arrow>
      <span>
        <Typography variant="body2" component="span">
          {usage.today.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          avg {avg7.toLocaleString()}/day, last 7 days
        </Typography>
      </span>
    </Tooltip>
  );
}

interface DataSourceTableProps {
  sources: DataSource[];
  onEdit: (source: DataSource) => void;
  onDelete: (source: DataSource) => void;
  /** Hide the edit/delete actions for every row. */
  readOnly?: boolean;
  /** Per-row permission — the combined Realtime tab mixes kinds the viewer
   *  may manage with kinds they may not (metered kinds not enabled for the
   *  tenant). Wins over `readOnly` when given. */
  canManage?: (source: DataSource) => boolean;
  /** Show the source type — needed where one table holds several. */
  showType?: boolean;
}

export default function DataSourceTable({
  sources,
  onEdit,
  onDelete,
  readOnly = false,
  canManage,
  showType = false,
}: DataSourceTableProps) {
  if (sources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
        No data sources found for this category.
      </Typography>
    );
  }

  const isRealtime = sources.some((s) => isRealtimeSourceType(s.source_type));
  const isMetered = sources.some((s) => METERED_ADAPTERS.has(s.adapter_type));

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            {showType && <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Type</TableCell>}
            <TableCell sx={{ fontWeight: 'bold' }}>Adapter</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>URL / Path</TableCell>
            {isRealtime && <TableCell sx={{ fontWeight: 'bold', width: 90 }}>Status</TableCell>}
            {isRealtime && <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Last Polled</TableCell>}
            {isMetered && (
              <TableCell sx={{ fontWeight: 'bold', width: 150 }}>
                <Tooltip
                  title="Xweather bills every request in accesses against your monthly allowance. Hover a value for the daily history."
                  arrow
                >
                  <span>Accesses today</span>
                </Tooltip>
              </TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Auth</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Scope</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sources.map((source) => (
            <TableRow key={source.id} hover>
              <TableCell>{source.name}</TableCell>
              {showType && (
                <TableCell>{SOURCE_TYPE_LABELS[source.source_type] ?? source.source_type}</TableCell>
              )}
              <TableCell>{ADAPTER_LABELS[source.adapter_type] ?? source.adapter_type}</TableCell>
              <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Tooltip title={source.api_url || source.file_path || '—'} arrow>
                  <span>{source.api_url || source.file_path || '—'}</span>
                </Tooltip>
              </TableCell>
              {isRealtime && (
                <TableCell>
                  <RealtimeStatusChip source={source} />
                </TableCell>
              )}
              {isRealtime && (
                <TableCell>{relativeTime(source.realtime_status?.last_polled_at)}</TableCell>
              )}
              {isMetered && (
                <TableCell>
                  {METERED_ADAPTERS.has(source.adapter_type) ? (
                    <UsageCell usage={source.realtime_status?.usage} />
                  ) : (
                    '—'
                  )}
                </TableCell>
              )}
              <TableCell>
                {source.has_credentials && (
                  <Tooltip title="Authenticated source" arrow>
                    <LockIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{source.tenant ? `Tenant #${source.tenant}` : 'Global'}</TableCell>
              <TableCell>
                {!source.is_builtin && (canManage ? canManage(source) : !readOnly) && (
                  <>
                    <IconButton size="small" onClick={() => onEdit(source)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(source)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
